import {
  getClauseContractFiles,
  getObligationContractFiles,
  getMatterContracts,
  getContractualPartyContracts,
  type RelatedContractFilesResponse,
  type ContractManagementContractSearchResult,
  type FilterableSearchQuery,
} from '@/api';

/**
 * Service for handling detail view operations for Contract Management entities.
 * Provides a centralized interface for fetching detail data with search, filtering, and pagination.
 */
export const ContractManagementDetailService = {
  /**
   * Get contract files for a Clause
   * @param uid - The unique identifier of the clause
   * @param searchQuery - Search query with text, filters, pagination, and sorting
   * @returns Promise that resolves with the detail response
   */
  async getClauseContractFiles(
    uid: string,
    searchQuery: FilterableSearchQuery,
    latestRevisionOnly?: boolean
  ): Promise<RelatedContractFilesResponse> {
    try {
      const response = await getClauseContractFiles({
        path: { uid },
        body: searchQuery,
        query:
          latestRevisionOnly === undefined
            ? undefined
            : { latest_revision_only: latestRevisionOnly },
      });

      if (!response.data) {
        throw new Error('No data returned from API');
      }

      return response.data;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to fetch clause contract files';
      throw new Error(errorMessage);
    }
  },

  /**
   * Get contract files for an Obligation
   * @param uid - The unique identifier of the obligation
   * @param searchQuery - Search query with text, filters, pagination, and sorting
   * @returns Promise that resolves with the detail response
   */
  async getObligationContractFiles(
    uid: string,
    searchQuery: FilterableSearchQuery,
    latestRevisionOnly?: boolean
  ): Promise<RelatedContractFilesResponse> {
    try {
      const response = await getObligationContractFiles({
        path: { uid },
        body: searchQuery,
        query:
          latestRevisionOnly === undefined
            ? undefined
            : { latest_revision_only: latestRevisionOnly },
      });

      if (!response.data) {
        throw new Error('No data returned from API');
      }

      return response.data;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to fetch obligation contract files';
      throw new Error(errorMessage);
    }
  },

  /**
   * Get contracts for a Matter
   * @param uid - The unique identifier of the matter
   * @param searchQuery - Search query with text, filters, pagination, and sorting
   * @returns Promise that resolves with the contract search result
   */
  async getMatterContracts(
    uid: string,
    searchQuery: FilterableSearchQuery
  ): Promise<ContractManagementContractSearchResult> {
    try {
      const response = await getMatterContracts({
        path: { uid },
        body: searchQuery,
      });

      if (!response.data) {
        throw new Error('No data returned from API');
      }

      return response.data;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to fetch matter contracts';
      throw new Error(errorMessage);
    }
  },

  /** Contracts with a file the contractual party is linked to. */
  async getContractualPartyContracts(
    uid: string,
    searchQuery: FilterableSearchQuery
  ): Promise<ContractManagementContractSearchResult> {
    const { data } = await getContractualPartyContracts({
      path: { uid },
      body: searchQuery,
      throwOnError: true,
    });
    return data;
  },
};
